import axios, {
  AxiosHeaders,
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import { useStoreSelectionStore } from '../stores/storeSelectionStore';
import { apiErrorCode, notifyDeviceAccessFailure } from './deviceAccessEvents';

// API base URL from environment
export const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Error response type
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

// Create axios instance
// Note: 60s timeout to accommodate long-running operations like placement options generation
// which involves polling Amazon's async API
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token getter - will be set by auth hook
let getAuthToken: (() => Promise<string | null>) | null = null;

/**
 * Set the auth token getter function
 * Called from the auth provider to inject Clerk's getToken
 */
export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  getAuthToken = getter;
}

function normalizeHeaderValue(value: unknown): string | null {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const parts = value.map((entry) => normalizeHeaderValue(entry)).filter((entry): entry is string => !!entry);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  return String(value);
}

function normalizeHeaderEntries(initialHeaders: unknown): Record<string, string> {
  if (!initialHeaders) {
    return {};
  }

  if (initialHeaders instanceof Headers) {
    return Object.fromEntries(initialHeaders.entries());
  }

  if (Array.isArray(initialHeaders)) {
    return initialHeaders.reduce<Record<string, string>>((acc, [key, value]) => {
      const normalized = normalizeHeaderValue(value);
      if (normalized != null) {
        acc[String(key)] = normalized;
      }
      return acc;
    }, {});
  }

  if (initialHeaders instanceof AxiosHeaders) {
    return normalizeHeaderEntries(initialHeaders.toJSON());
  }

  if (typeof initialHeaders === 'object') {
    const toJson = (initialHeaders as { toJSON?: () => unknown }).toJSON;
    if (typeof toJson === 'function') {
      return normalizeHeaderEntries(toJson.call(initialHeaders));
    }

    return Object.entries(initialHeaders as Record<string, unknown>).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        const normalized = normalizeHeaderValue(value);
        if (normalized != null) {
          acc[key] = normalized;
        }
        return acc;
      },
      {}
    );
  }

  return {};
}

function findHeaderKey(headers: Record<string, string>, headerName: string): string | undefined {
  const lowerName = headerName.toLowerCase();
  return Object.keys(headers).find((key) => key.toLowerCase() === lowerName);
}

// Resolves an auth token even if the React effect in App.tsx hasn't yet wired up
// setAuthTokenGetter — on a fresh mount, React Query fires its first queries
// before that effect runs, which previously meant those requests went out with
// no Authorization header. Falling back to window.Clerk when the setter isn't
// registered yet closes that race.
async function resolveAuthToken(): Promise<string | null> {
  if (getAuthToken) {
    try {
      return await getAuthToken();
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }
  const clerk = (window as unknown as {
    Clerk?: { session?: { getToken?: () => Promise<string | null> } };
  }).Clerk;
  try {
    return (await clerk?.session?.getToken?.()) ?? null;
  } catch {
    return null;
  }
}

export async function buildApiHeaders(
  initialHeaders: HeadersInit | Record<string, unknown> = {}
): Promise<Record<string, string>> {
  const headers = normalizeHeaderEntries(initialHeaders);
  const legacyAccountHeader = findHeaderKey(headers, 'x-account-id');
  if (legacyAccountHeader) {
    delete headers[legacyAccountHeader];
  }

  // Bind the request before asynchronous auth; a store switch must not retarget it.
  const currentStoreId = useStoreSelectionStore.getState().currentStoreId;
  const token = await resolveAuthToken();
  if (token && !findHeaderKey(headers, 'Authorization')) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (currentStoreId && !findHeaderKey(headers, 'x-store-id')) {
    headers['x-store-id'] = currentStoreId;
  }

  return headers;
}

// Request interceptor - add auth headers and store context
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const headers = await buildApiHeaders(config.headers);
    for (const [key, value] of Object.entries(headers)) {
      if (typeof config.headers.set === 'function') {
        config.headers.set(key, value);
      } else {
        (config.headers as Record<string, string>)[key] = value;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<ApiError>) => {
    const { response } = error;
    // Recovery screens own their inline errors; broadcasting these would replace
    // a failed logout row with the generic verification screen.
    if (!error.config?.url?.startsWith('/api/auth/devices')) {
      notifyDeviceAccessFailure(apiErrorCode(error));
    }

    // Promote the server-supplied error message into error.message so generic
    // consumers like `toast.error(err.message)` show the real backend error
    // instead of axios's default "Request failed with status code 500".
    // Backend shape: { success: false, error: { code, message, details? } }
    // Legacy shapes also seen: { error: 'string' } and { message: 'string' }.
    if (response?.data) {
      const data = response.data as {
        error?: string | { code?: string; message?: string; details?: unknown };
        message?: string;
      };
      let extracted: string | undefined;
      if (typeof data.error === 'string') {
        extracted = data.error;
      } else if (data.error && typeof data.error === 'object') {
        extracted = data.error.message;
      } else if (data.message) {
        extracted = data.message;
      }
      if (extracted && extracted.trim().length > 0) {
        // Keep axios's default in `error.cause`-style breadcrumb for debugging.
        (error as AxiosError & { originalMessage?: string }).originalMessage = error.message;
        error.message = extracted;
      }
    }

    // Handle specific error codes
    if (axios.isCancel(error)) {
      // Query observers cancel requests during range switches and unmounts.
      // This is expected control flow, not a network outage.
    } else if (response) {
      switch (response.status) {
        case 401:
          // Unauthorized - token expired or invalid
          // Clerk should handle token refresh automatically
          // If this happens, user needs to re-authenticate
          console.error('Authentication error - please sign in again');
          break;

        case 403:
          // Forbidden - user doesn't have access
          console.error('Access denied to this resource');
          break;

        case 404:
          // Not found
          console.error('Resource not found');
          break;

        case 422:
          // Validation error
          console.error('Validation error:', response.data?.details);
          break;

        case 429:
          // Rate limited
          console.error('Rate limited - please try again later');
          break;

        case 500:
        case 502:
        case 503: {
          // Server error — surface the actual server-supplied message and
          // structured details so the UI can show what really went wrong
          // instead of a generic "try again later". Backend errorHandler
          // shape: { success: false, error: { code, message, details? } }
          // but legacy routes also use { error: 'string' } and
          // { error: { message } } so handle all three.
          const data = response.data as {
            error?: string | { code?: string; message?: string; details?: unknown };
            message?: string;
            details?: unknown;
          } | undefined;
          let serverMessage: string | undefined;
          let serverDetails: unknown;
          if (typeof data?.error === 'string') {
            serverMessage = data.error;
            serverDetails = data.details;
          } else if (data?.error && typeof data.error === 'object') {
            serverMessage = data.error.message;
            serverDetails = data.error.details;
          } else if (data?.message) {
            serverMessage = data.message;
            serverDetails = data.details;
          }
          if (serverMessage) {
            console.error(
              `Server error (${response.status}): ${serverMessage}`,
              serverDetails ?? '',
            );
          } else {
            console.error(`Server error (${response.status}) - no details from server`);
          }
          break;
        }
      }
    } else if (error.code === 'ECONNABORTED') {
      // Timeout — include the URL so it's clear which endpoint timed out
      // (LTL transportation-options can legitimately take 60-120s).
      const cfg = error.config as { url?: string; timeout?: number } | undefined;
      console.error(
        `Request timeout after ${cfg?.timeout ?? 'default'}ms`,
        cfg?.url ? `url=${cfg.url}` : '',
      );
    } else if (!error.response) {
      // Network error
      console.error('Network error - please check your connection');
    }

    return Promise.reject(error);
  }
);

/**
 * Like getErrorMessage, but prefers field-level validation issue text over the
 * generic "Request body validation failed" wrapper. The backend's validation
 * middleware nests the real, user-facing reason under
 * error.response.data.error.details.issues[].message (e.g. the bulk box-count
 * cap), which getErrorMessage alone never surfaces.
 */
export function getApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { error?: { details?: { issues?: Array<{ message?: string }> } } }
      | undefined;
    const issues = data?.error?.details?.issues;
    if (Array.isArray(issues) && issues.length > 0) {
      const messages = issues
        .map((issue) => issue?.message)
        .filter((m): m is string => typeof m === 'string' && m.trim().length > 0);
      if (messages.length > 0) return messages.join(' ');
    }
  }
  return getErrorMessage(error);
}

// Helper to extract error message from API response
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;
    // Check both flat and nested error response formats
    // Backend returns: { error: { code, message } } or { message }
    const data = axiosError.response?.data as { message?: string; error?: { message?: string } } | undefined;
    return data?.error?.message || data?.message || axiosError.message || 'An error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
}

/**
 * Extract Amazon SP-API error details from an API error response.
 * Backend wraps SP-API failures in SpApiError which puts the serialized
 * library error at error.response.data.error.details.amazonError.
 * Returns null when the error is not an SP-API error.
 */
export function getAmazonError(error: unknown): {
  amazonError: {
    message?: string;
    code?: string | null;
    details?: unknown;
    response?: { status: number | null; data: unknown } | null;
  };
  context?: Record<string, unknown>;
} | null {
  if (!axios.isAxiosError(error)) return null;
  const data = error.response?.data as
    | { error?: { details?: { amazonError?: unknown; context?: unknown } } }
    | undefined;
  const amazonError = data?.error?.details?.amazonError as
    | { message?: string; code?: string | null; details?: unknown; response?: { status: number | null; data: unknown } | null }
    | undefined;
  if (!amazonError) return null;
  return {
    amazonError,
    context: data?.error?.details?.context as Record<string, unknown> | undefined,
  };
}

/**
 * Best-effort extraction of affected SKUs from any Amazon error response.
 * Mirrors the backend's extractSkuFromOperationProblem logic so the UI can
 * highlight the offending rows in the items table.
 *
 * Looks at:
 *   - error.response.data.error.details.affectedSkus (deduped list, set by
 *     SpApiError when the backend already extracted them)
 *   - error.response.data.error.details.amazonError.details.problems[] —
 *     each problem's msku, sku, or details/message text matching either
 *     "resource '<sku>'" or "(msku|sku): <sku>"
 *   - the top-level error message text (last-resort regex)
 *
 * Returns a unique-preserved array of SKUs (empty if none found).
 */
export function getAmazonErrorSkus(error: unknown): string[] {
  if (!axios.isAxiosError(error)) {
    const localAffectedSkus = error && typeof error === 'object'
      ? (error as { affectedSkus?: unknown }).affectedSkus
      : null;
    return Array.isArray(localAffectedSkus)
      ? [...new Set(localAffectedSkus.filter((sku): sku is string => typeof sku === 'string'))]
      : [];
  }
  const data = error.response?.data as
    | {
        error?: {
          message?: string;
          details?: {
            affectedSkus?: string[];
            amazonError?: { details?: { problems?: Array<unknown> } };
          };
        };
      }
    | undefined;

  const seen = new Set<string>();
  const push = (sku: string | null | undefined) => {
    if (sku && typeof sku === 'string') seen.add(sku);
  };

  // 1. Backend-precomputed list (when SpApiError was constructed with affectedSkus).
  if (Array.isArray(data?.error?.details?.affectedSkus)) {
    for (const s of data!.error!.details!.affectedSkus!) push(s);
  }

  // 2. Per-problem extraction matching the backend helper.
  const problems = data?.error?.details?.amazonError?.details as
    | { problems?: Array<{ msku?: string; sku?: string; details?: string; message?: string }> }
    | undefined;
  if (Array.isArray(problems?.problems)) {
    for (const p of problems!.problems!) {
      if (p.msku) { push(p.msku); continue; }
      if (p.sku) { push(p.sku); continue; }
      if (p.details) {
        const m1 = p.details.match(/resource\s+'([^']+)'/i);
        if (m1) { push(m1[1]); continue; }
        const m2 = p.details.match(/(?:msku|sku)[:\s]+([A-Za-z0-9_.-]+)/i);
        if (m2) { push(m2[1]); continue; }
      }
      if (p.message) {
        const m3 = p.message.match(/(?:msku|sku)[:\s]+([A-Za-z0-9_.-]+)/i);
        if (m3) push(m3[1]);
      }
    }
  }

  // 3. Last-resort regex on the top-level message.
  const topMessage = data?.error?.message;
  if (topMessage && seen.size === 0) {
    const m = topMessage.match(/\[SKU:\s*([^\]]+)\]/g);
    if (m) {
      for (const tag of m) {
        const inner = tag.match(/\[SKU:\s*([^\]]+)\]/);
        if (inner) push(inner[1].trim());
      }
    }
  }

  return Array.from(seen);
}

// Helper to check if error is a specific status code
export function isStatusError(error: unknown, status: number): boolean {
  if (axios.isAxiosError(error)) {
    return error.response?.status === status;
  }
  return false;
}

export function isTransientGatewayError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;

  const status = error.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return true;
  }

  return !error.response || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK';
}

// Typed API methods
export const apiClient = {
  get: <T>(url: string, config?: Parameters<typeof api.get>[1]) =>
    api.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: Parameters<typeof api.post>[2]) =>
    api.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: Parameters<typeof api.put>[2]) =>
    api.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: Parameters<typeof api.patch>[2]) =>
    api.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: Parameters<typeof api.delete>[1]) =>
    api.delete<T>(url, config).then((res) => res.data),
};

// Export the raw axios instance for advanced use cases
export { api };
export default apiClient;
